import { FragmentOf, graphql, readFragment } from '@/lib/graphql';
import { format } from '@formkit/tempo';
import { useTranslation } from 'react-i18next';

const formatDate = (value: string | null | undefined) => {
  if (!value) return '-';

  try {
    return format(new Date(value), 'YYYY-MM-DD');
  } catch {
    return '-';
  }
};

const organizationTableRowFragment = graphql(`
  fragment OrganizationTableRowFragment on Organization {
    id
    name
    originalName
    abbreviation
    status
    type
    country
    countryTags
    latestArticleDate
    oldestArticleDate
  }
`);

type OrganizationTableRowProps = {
  data: FragmentOf<typeof organizationTableRowFragment>;
};

export const OrganizationTableRow = ({ data }: OrganizationTableRowProps) => {
  const organization = readFragment(organizationTableRowFragment, data);
  const { i18n } = useTranslation();

  const isJapaneseLocale = i18n.language?.toLowerCase().startsWith('ja');
  const localizedName = organization.name?.trim();
  const originalName = organization.originalName?.trim() ?? '-';
  const displayAbbreviation = organization.abbreviation?.trim();

  const primaryName = originalName;
  const secondaryName =
    isJapaneseLocale && localizedName && localizedName !== primaryName
      ? localizedName
      : undefined;

  return (
    <tr>
      <td className="p-4 align-top">
        <div className="flex flex-col gap-1 text-sm">
          <span className="font-semibold text-gray-900">
            {primaryName}
            {displayAbbreviation ? `（${displayAbbreviation}）` : ''}
          </span>
          {secondaryName && (
            <span className="text-xs text-gray-600">{secondaryName}</span>
          )}
        </div>
      </td>
      <td className="p-4 align-top text-sm text-gray-700">
        {organization.status || '-'}
      </td>
      <td className="p-4 align-top text-sm text-gray-700">
        {organization.type || '-'}
      </td>
      <td className="p-4 align-top text-sm text-gray-700">
        {organization.country || '-'}
      </td>
      <td className="p-4 align-top text-sm text-gray-700">
        {organization.countryTags && organization.countryTags.length > 0
          ? organization.countryTags.join(', ')
          : '-'}
      </td>
      <td className="p-4 align-top text-sm text-gray-700">
        {formatDate(organization.latestArticleDate)}
      </td>
      <td className="p-4 align-top text-sm text-gray-700">
        {formatDate(organization.oldestArticleDate)}
      </td>
    </tr>
  );
};

OrganizationTableRow.fragments = {
  organization: organizationTableRowFragment,
};
